import { useEffect, useMemo } from "react";
import Experiment from "../Experiment/Experiment";
import { useEv2ExperimentParams } from "../Hooks/UseCurrentExtension";
import { useNavigateEv2Experiments } from "../Hooks/UseNavigateToEv2Experiments";
import { useAppContext } from "../Contexts";
import { mergeStyleSets, Stack } from "@fluentui/react";
import { SessionProvider } from "../Contexts/SessionContext";
import ExperimentsTablePage from "../ExperimentsTable/ExperimentsTablePage";

interface EV2ExperimentsContentProps {
  /**
   * When true show experiment creation form
   */
  isCreate?: boolean;
  /**
   * When true show experiment cloning form with pre-filled data
   */
  isClone?: boolean;
  /**
   * When true will direct users to select an extension.
   */
  isRoot?: boolean;
}

/**
 * Content router component that renders either the Experiment view or ExperimentsTable
 * based on URL parameters and props. Handles title updates and view selection logic.
 */
const EV2ExperimentsContent: React.FC<EV2ExperimentsContentProps> = ({
  isCreate,
  isClone,
  isRoot,
}) => {
  const {
    extensionName = "",
    extensionParamName,
    extensionContextName,
    experimentName = "",
  } = useEv2ExperimentParams();
  const { setExtensionName } = useAppContext();
  const { navigateToExperiment } = useNavigateEv2Experiments();

  useEffect(() => {
    // If extensionName is set but not in URL, navigate to it
    if (extensionName && !extensionParamName) {
      navigateToExperiment(extensionName);
      // If extension in context differs from URL param, update context
    } else if (extensionContextName !== extensionParamName) {
      setExtensionName(extensionName);
    }
  }, [
    extensionName,
    extensionParamName,
    extensionContextName,
    navigateToExperiment,
    setExtensionName,
  ]);

  const content = useMemo(() => {
    if (extensionName && (experimentName || isCreate)) {
      return <Experiment isCreate={!!isCreate} isClone={!!isClone} />;
    }
    return <ExperimentsTablePage isRoot={isRoot} />;
  }, [extensionName, experimentName, isRoot, isCreate, isClone]);

  return (
    <SessionProvider>
      <Stack className={styles.ev2ExperimentContextContainer}>{content}</Stack>
    </SessionProvider>
  );
};

const styles = mergeStyleSets({
  ev2ExperimentContextContainer: {
    height: "100%",
    minHeight: 0,
  },
});

export default EV2ExperimentsContent;
